import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, Form, Row, Col, Badge, Alert } from 'react-bootstrap';
import { useFormik } from 'formik';
import { onboardBusiness, syncStripeStatus } from '../../../../redux/reducers/transactionSlice';
import { getBusinessById, updatePaymentPolicy } from '../../../../redux/reducers/businessSlice';
import {
    LuCircleCheck, LuCircleAlert, LuRefreshCw,
    LuCreditCard, LuInfo, LuShieldCheck
} from 'react-icons/lu';
import { toast } from 'react-toastify';
import Spinner from '../../../../components/Spinner';

const PaymentSetting = ({ data: businessDetail }) => {
    const dispatch = useDispatch();
    const { error: stripeError } = useSelector((state) => state.transaction);
    const { loading: businessLoading } = useSelector((state) => state.business);

    const [connectLoading, setConnectLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    const { stripeConfig, settings, _id: businessId } = businessDetail || {};

    // Logic check: Is the business actually capable of taking money?
    const isSetup = stripeConfig?.onboardingComplete && stripeConfig?.chargesEnabled;

    // Auto-sync if user just returned from Stripe
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('stripe') === 'success') {
            handleSyncStatus();
            // Clean URL to prevent repeated syncs on refresh
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const formik = useFormik({
        initialValues: {
            requirePaymentUpfront: settings?.paymentPolicy?.requirePaymentUpfront || false,
            paymentType: settings?.paymentPolicy?.paymentType || 'full',
            stripeFeePayer: settings?.paymentPolicy?.stripeFeePayer || 'business',
            payOnDelivery: settings?.paymentPolicy?.payOnDelivery ?? true,
            acceptChequeOrCash: settings?.paymentPolicy?.acceptChequeOrCash ?? true,
        },
        enableReinitialize: true,
        onSubmit: (values) => {
            dispatch(updatePaymentPolicy({ id: businessId, data: values }))
                .unwrap()
                .then(() => toast.success("Policies updated"));
        },
    });

    const handleConnectStripe = async () => {
        setConnectLoading(true);
        try {
            const result = await dispatch(onboardBusiness(businessId)).unwrap();
            if (result?.url) window.location.href = result.url;
        } finally {
            setConnectLoading(false);
        }
    };

    const handleSyncStatus = async () => {
        setSyncLoading(true);
        try {
            await dispatch(syncStripeStatus(businessId)).unwrap();
            await dispatch(getBusinessById(businessId)); // Refresh global business state
            toast.success("Stripe status updated!");
        } catch (err) {
            toast.error(err || "Sync failed");
        } finally {
            setSyncLoading(false);
        }
    };

    return (
        <Spinner active={businessLoading || connectLoading || syncLoading}>
            <div className="py-2">
                {/* 1. Stripe Connection Status Card */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                    <Card.Header className="bg-light border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="fw-bold mb-0 text-brand">Bank Connection</h5>
                            <p className="text-muted small mb-0">Manage your Stripe Connect account</p>
                        </div>
                        <Badge bg={isSetup ? "soft-success" : "soft-warning"} className={`${isSetup ? 'text-success' : 'text-warning'} rounded-pill px-3 py-2`}>
                            {isSetup ? <><LuCircleCheck /> Connected</> : <><LuCircleAlert /> Setup Required</>}
                        </Badge>
                    </Card.Header>
                    <Card.Body className="p-4">
                        {!isSetup ? (
                            <div className="text-center py-3">
                                <div className="text-brand mb-3 opacity-25"><LuCreditCard size={50} /></div>
                                <h5 className="fw-bold">Enable Online Payments</h5>
                                <p className="text-muted small mx-auto mb-4" style={{ maxWidth: '350px' }}>
                                    You must complete Stripe onboarding to enable upfront payments and automatic payouts.
                                </p>
                                <div className="d-flex justify-content-center gap-2">
                                    <Button variant="brand" className="rounded-pill px-4 fw-bold" onClick={handleConnectStripe} disabled={connectLoading}>
                                        {connectLoading ? "Connecting..." : "Setup Payouts with Stripe"}
                                    </Button>
                                    {stripeConfig?.stripeConnectId && (
                                        <Button variant="outline-secondary" className="rounded-pill px-3" onClick={handleSyncStatus} disabled={syncLoading}>
                                            <LuRefreshCw className={syncLoading ? 'animate-spin' : ''} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex align-items-center justify-content-between bg-soft-success p-3 rounded-4 border border-success border-opacity-10">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-success text-white p-2 rounded-circle"><LuShieldCheck size={20} /></div>
                                    <div>
                                        <div className="small fw-bold text-success">Verified Business Account</div>
                                        <div className="text-muted smaller font-monospace">{stripeConfig.stripeConnectId}</div>
                                    </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={handleSyncStatus} disabled={syncLoading}>
                                    <LuRefreshCw size={18} className={syncLoading ? 'animate-spin' : ''} />
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* 2. Payment Policies Form */}
                <Form onSubmit={formik.handleSubmit}>
                    <Card className={`border-0 shadow-sm rounded-4 p-4 mb-4 ${!isSetup && formik.values.requirePaymentUpfront ? 'opacity-75' : ''}`}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="fw-bold mb-0 text-brand">Payment Policies</h5>
                            {formik.dirty && <Button type="submit" variant="brand" size="sm" className="rounded-pill px-4 fw-bold shadow-sm" disabled={businessLoading}>Save Changes</Button>}
                        </div>

                        {!isSetup && (
                            <Alert variant="info" className="border-0 rounded-4 small py-2">
                                <LuInfo className="me-2" /> Upfront payments are disabled until your bank is connected.
                            </Alert>
                        )}

                        <Row className="gy-4">
                            <Col md={12}>
                                <div className={`d-flex justify-content-between align-items-center p-3 rounded-4 bg-white border`}>
                                    <div>
                                        <div className="fw-bold small">Require Payment Upfront</div>
                                        <div className="text-muted smaller">Force customers to pay via card to secure a slot.</div>
                                    </div>
                                    <Form.Check
                                        type="switch"
                                        id="requirePaymentUpfront"
                                        name="requirePaymentUpfront"
                                        disabled={!isSetup}
                                        checked={formik.values.requirePaymentUpfront}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="d-flex justify-content-between align-items-center p-3 rounded-4 bg-white border h-100">
                                    <div>
                                        <div className="fw-bold small">Accept Cheque or Cash</div>
                                        <div className="text-muted smaller">Allow customers to pay via offline methods.</div>
                                    </div>
                                    <Form.Check
                                        type="switch"
                                        id="acceptChequeOrCash"
                                        name="acceptChequeOrCash"
                                        checked={formik.values.acceptChequeOrCash}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="d-flex justify-content-between align-items-center p-3 rounded-4 bg-white border h-100">
                                    <div>
                                        <div className="fw-bold small">Cash On Delivery</div>
                                        <div className="text-muted smaller">Display "Cash On Delivery" option at checkout.</div>
                                    </div>
                                    <Form.Check
                                        type="switch"
                                        id="payOnDelivery"
                                        name="payOnDelivery"
                                        checked={formik.values.payOnDelivery}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </Col>

                            <Col md={6}>
                                <Form.Label className="small fw-bold mb-2">Transaction Fee Payer</Form.Label>
                                <div className="d-flex bg-light p-1 rounded-pill">
                                    {['business', 'customer'].map((type) => (
                                        <Button
                                            key={type}
                                            variant={formik.values.stripeFeePayer === type ? 'brand' : 'light'}
                                            className="flex-grow-1 rounded-pill py-2 border-0 small fw-bold text-capitalize"
                                            onClick={() => formik.setFieldValue('stripeFeePayer', type)}
                                        >
                                            {type}
                                        </Button>
                                    ))}
                                </div>
                                <div className="smaller text-muted mt-2 px-2">
                                    <LuInfo size={18} /> Stripe fee is typically 2.9% + 30¢
                                </div>
                            </Col>

                            <Col md={6}>
                                <Form.Label className="small fw-bold mb-2">Charge Amount</Form.Label>
                                <Form.Select
                                    name="paymentType"
                                    className="bg-light border-0 py-2 rounded-pill small fw-bold"
                                    value={formik.values.paymentType}
                                    onChange={formik.handleChange}
                                >
                                    <option value="full">Full Amount (100%)</option>
                                    <option value="deposit">Deposit Only</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Card>
                </Form>
            </div>
        </Spinner>
    );
};

export default PaymentSetting;